import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export function EnquiryDialog({ trigger, college, defaultCourse = "MBA" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", course: defaultCourse,
    entrance_exam: "", percentile: "", budget: "", consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the consent to continue"); return; }
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        college: college?.name,
        college_slug: college?.slug,
      });
      toast.success("Enquiry submitted! Our counsellor will reach out soon.");
      setOpen(false);
      setForm({ name: "", mobile: "", email: "", city: "", course: defaultCourse, entrance_exam: "", percentile: "", budget: "", consent: false });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">
            {college ? `Enquire about ${college.short_name || college.name}` : "Get College Recommendations"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Full Name *</Label>
              <Input required data-testid="enquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
            <div><Label>Mobile *</Label>
              <Input required data-testid="enquiry-mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="10-digit mobile" /></div>
            <div><Label>Email</Label>
              <Input type="email" data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" /></div>
            <div><Label>City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Your city" /></div>
            <div><Label>Course</Label>
              <Input value={form.course} onChange={(e) => set("course", e.target.value)} placeholder="MBA / PGDM" /></div>
            <div><Label>Entrance Exam</Label>
              <Input value={form.entrance_exam} onChange={(e) => set("entrance_exam", e.target.value)} placeholder="CAT / XAT" /></div>
            <div><Label>Percentile</Label>
              <Input value={form.percentile} onChange={(e) => set("percentile", e.target.value)} placeholder="e.g. 92" /></div>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
            <Checkbox data-testid="enquiry-consent" checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
            <span>I authorise LockMyCollege and its partner institutes to contact me via call, SMS, email and WhatsApp regarding my enquiry. I have read the Privacy Policy.</span>
          </label>
          <button type="submit" disabled={loading} data-testid="enquiry-submit"
            className="w-full gold-btn rounded-full py-3 font-semibold flex items-center justify-center gap-2">
            {loading && <Loader2 className="animate-spin" size={16} />} Submit Enquiry
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
